/**
 * Live-summary helpers for the collapsed "Pensando" activity group.
 *
 * The group header shows "Pensando" until the serialized activity reaches
 * SUMMARY_FIRST_CHARS; then a <=8-word summary is requested (sabiazinho-4 via
 * /api/chat/activity_summary) and refreshed every SUMMARY_REFRESH_CHARS new
 * chars while the turn is still streaming.
 */

// X: below this many chars of activity the header shows a generic "Pensando".
// Tool-only bursts serialize short (~40 chars per call), so keep X low enough
// that a 2-3 tool turn still gets a summary.
export const SUMMARY_FIRST_CHARS = 150;
// Y: how many NEW chars must accumulate before the summary is refreshed.
export const SUMMARY_REFRESH_CHARS = 400;
// Floor between sabiazinho calls: the FIRST summary fires as soon as X is
// reached; refreshes additionally wait this long since the previous call
// (reasoning streams fast — chars alone would refire back-to-back).
export const SUMMARY_MIN_INTERVAL_MS = 8000;

// O que cada ferramenta faz, em palavras do usuário: com o nome interno
// ("tool_discovery") o resumidor chutava pelo assunto e etapas diferentes
// (procurar ferramentas × consultar a bula) saíam com a mesma frase.
const TOOL_ACTION: Record<string, string> = {
  tool_discovery: 'procurando quais ferramentas usar',
  skill_read: 'carregando instruções especializadas',
  data_ocean_query: 'consultando base de dados',
  web_search: 'pesquisando na web',
  page_scraping: 'lendo página da web',
  erp_query: 'consultando o ERP',
  erp_create_invoice: 'emitindo nota fiscal',
  datajud_query: 'consultando processos no DataJud',
  buscar_conversas: 'buscando nas conversas anteriores',
};

/** Serialize the burst (reasoning + tool/code steps) into plain text for the summarizer. */
export function buildActivityText(items: any[]): string {
  const parts: string[] = [];
  for (const it of items || []) {
    if (!it) continue;
    if (it.type === 'reasoning') {
      const t = (it.reasoning || '').trim();
      if (t) parts.push(t);
    } else if (it.type === 'narration') {
      const t = (it.text || '').trim();
      if (t) parts.push(`[mensagem ao usuário] ${t}`);
    } else if (it.type === 'tool_action') {
      const qs = (it.queries && it.queries.length ? it.queries : [it.label]).filter(Boolean);
      const what = TOOL_ACTION[it.tool] || it.tool;
      parts.push(`[ação: ${what}] ${qs.join(' | ')}`.trim());
    } else if (it.type === 'code_execution') {
      const a = it.args || {};
      const what = a.command || a.code || a.path || '';
      parts.push(`[ação: executando código] ${String(what).slice(0, 300)}`.trim());
    }
  }
  return parts.join('\n');
}

/** Should we (re)fire the summarizer for the current activity length? */
export function shouldSummarize(
  len: number,
  lastLen: number | null,
  msSinceLastCall: number = Infinity,
  compact = false,
): boolean {
  if (len < (compact ? COMPACT_FIRST_CHARS : SUMMARY_FIRST_CHARS)) return false;
  if (lastLen === null) return true; // first call: only the size gate
  return compact
    ? len - lastLen >= COMPACT_REFRESH_CHARS && msSinceLastCall >= COMPACT_MIN_INTERVAL_MS
    : len - lastLen >= SUMMARY_REFRESH_CHARS && msSinceLastCall >= SUMMARY_MIN_INTERVAL_MS;
}

// Status do chat do Sabiá 5 (modo compacto): o status é a única coisa na tela
// durante o trabalho, então acompanha mais de perto — o resumo (Sabiá 5 sem
// raciocínio, ~0,4 s) sai cedo e é refeito a cada ~250 novos caracteres.
export const COMPACT_FIRST_CHARS = 60;
export const COMPACT_REFRESH_CHARS = 250;
export const COMPACT_MIN_INTERVAL_MS = 3000;

/** "42 s", "2 min 05 s", "1 h 03 min". */
export function formatDuration(ms: number): string {
  const total = Math.max(1, Math.round(ms / 1000));
  if (total < 60) return `${total} s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
  return `${m} min ${String(sec).padStart(2, '0')} s`;
}
